"use client"

import type {
  WorkflowItem,
  WorkflowStage,
  WorkflowApiResponse,
  FieldConfiguration,
  WorkflowConfiguration,
  AssignedUser,
  WorkflowSearchRequest,
  SuggestionsApiResponse,
  UserSearchResponse,
  UserSearchApiResponse,
  UserReportDto,
  ClientCustomFieldsResponse
} from "@/types/workflow"
import { resolveStageTransitionItemId } from "@/lib/action-utils"
import type {
  UserViewsResponse,
  SaveViewSettingsRequestDto,
  SaveViewGroupRequestDto,
  SaveViewGroupResponseDto,
} from "@/types/saved-view"
import type { VmsEmailPreview } from "@/types/vms/vms-requests"
import type { RelatedRequestSource } from "@/types/client-requests"
import type { ChangeRequestDto } from "@/types/client-requests"
import { resolveClientRequestType } from "@/types/client-requests"

// Mirrors the server-side RelatedRequestDto exactly — ASP.NET Core serializes
// camelCase by default, so no PascalCase aliasing is needed. The shape was
// realigned (workerFullName / startDate / endDate / billRate) when the
// backend retired its `PrimaryBillRate` field and standardised on canonical
// names; field names here must match what the backend actually emits or the
// transform below ends up with `undefined` everywhere.
interface RelatedRequestApiDto {
  source: RelatedRequestSource
  id: string
  requestId: string | null
  requestType: string | null
  status: string | null
  workerFullName: string | null
  requestedByName: string | null
  requestedByEmail: string | null
  requestDate: string | null
  requestCompleted: string | null
  clientOrganization: string | null
  workerOrganization: string | null
  lineOfBusiness: string | null
  workerProfileType: string | null
  startDate: string | null
  endDate: string | null
  billRate: number | null
  version: number | null
  atsPlacementId: number | null
  atsSource: string | null
  jobTitle: string | null
  preVerificationRequestId: string | null
  atsStatus: string | null
  workerEmail: string | null
  // VmsRequest-only — null on other Source families. Lets the related-requests
  // panel surface a "View original email" link that opens the existing email
  // preview side-panel (same id consumed by /api/vms/emails/{id}/preview).
  linkedEmailId: string | null
}
import { useCallback } from "react"
import { useApi } from "@/lib/api"
import { useEntityType } from "@/contexts/entity-type-context"
import { logger } from "@/lib/logger"
import { coalesceWorkflowFetch } from "@/hooks/workflow-fetch-cache"

// Field mapping for the API select statement
const API_SELECT_FIELDS = [
  'id',
  'WorkflowHubId',
  'Actions',
  'Stage',
  'HealthStatus',
  'WorkerFullName',
  'JobTitle',
  'WorkerPhone',
  'WorkerEmail',
  'ProfileType',
  'EligibilityToWork',
  'PermitExpirationDate',
  'SupplierName',
  'ClientName',
  'InternalCompany',
  'Branch',
  'StartDate',
  'EndDate',
  'WorkerLocation',
  'PrimaryJobOwnerFullName',
  'RecruiterFullName',
  'AtsPlacementId',
  'VmsId',
  'PrimaryPayRate',
  'PrimaryBillRate',
  'OtEligible',
  'OtPayRate',
  'OtBillRate',
  'TimesheetMethod',
  'ManagerFullName',
  'ManagerEmail',
  'Duration',
  'WorkerProfileStatus',
  'AssignedToUser'
].join(',')

// Maps RelatedRequestDto -> ChangeRequestDto. The two shapes still differ on
// a few field names (server: workerFullName/clientOrganization, grid:
// workerName/clientOrg) for historical reasons in the grid contract, hence
// the explicit per-field rename. Source-side fields now match the canonical
// names emitted by the backend after the PrimaryBillRate/JobStart/JobEnd
// rename, so no fallback to legacy aliases is needed here.
function transformRelatedRequestToChangeRequest(item: RelatedRequestApiDto): ChangeRequestDto {
  return {
    source: item.source,
    changeRequestId: item.id,
    requestId: item.requestId ?? undefined,
    requestType: resolveClientRequestType(item.requestType),
    status: (item.status as ChangeRequestDto['status']) ?? undefined,
    workerName: item.workerFullName ?? undefined,
    requestedByName: item.requestedByName ?? undefined,
    requestedByEmail: item.requestedByEmail ?? undefined,
    dateRequested: item.requestDate ?? undefined,
    dateCompleted: item.requestCompleted ?? undefined,
    clientOrg: item.clientOrganization ?? undefined,
    workerOrg: item.workerOrganization ?? undefined,
    lineOfBusiness: item.lineOfBusiness ?? undefined,
    profileType: item.workerProfileType ?? undefined,
    startDate: item.startDate ?? undefined,
    endDate: item.endDate ?? undefined,
    billRate: item.billRate ?? undefined,
    version: item.version ?? undefined,
    atsPlacementId: item.atsPlacementId ?? undefined,
    atsSource: item.atsSource ?? undefined,
    jobTitle: item.jobTitle ?? undefined,
    preVerificationRequestId: item.preVerificationRequestId ?? undefined,
    atsStatus: item.atsStatus ?? undefined,
    workerEmail: item.workerEmail ?? undefined,
    linkedEmailId: item.linkedEmailId ?? undefined,
  }
}

// Utility functions
const transformApiResponseToWorkflowItem = (apiItem: any): WorkflowItem => {
  // Preserve all fields from API response exactly as they are
  // The spread operator comes first to keep original field names and casing
  return {
    ...apiItem,
    // Only override/add computed fields that don't exist in API response
    id: apiItem.id || apiItem.atsPlacementId || apiItem.vmsId || `item-${Date.now()}-${Math.random()}`,
    stage: apiItem.stage || mapWorkOrderStatusToStage(apiItem.workOrderStatus)
  }
}

const mapWorkOrderStatusToStage = (workOrderStatus?: string): WorkflowStage => {
  if (!workOrderStatus) return 'onboarding'
  
  const status = workOrderStatus.toLowerCase()
  
  if (status.includes('active') || status.includes('assignment')) return 'active'
  if (status.includes('extending') || status.includes('extension')) return 'extension'
  if (status.includes('offboarding') || status.includes('terminating')) return 'offboarding'
  if (status.includes('complete') || status.includes('closed')) return 'complete'
  
  return 'onboarding' // Default
}

const buildFilterFromStageAndFilters = (
  stage: WorkflowStage | undefined,
  customFilter: string[] | undefined,
  entityType?: string
): string | null => {
  const filterConditions: string[] = []

  // VMS sub-tabs + ClientRequest tab all live on the shared client-request index;
  // their docs don't have a `DocumentType eq 'Assignment'` discriminator and don't
  // carry a `Stage` column at all (each entity is collapsed to a single stage with
  // no FixedFilter). The backend's Search{Vms,ClientRequest}Async methods add the
  // entity-scoped DocumentType clause unconditionally, so the frontend just
  // forwards user/column filters here.
  // Entities that live on the shared gw-client-request-index (or, for VmsEmail, another
  // single-stage index): no `DocumentType eq 'Assignment'` discriminator and no `Stage`
  // column. The backend's Search{Vms,ClientRequest,AtsPlacementRequest}Async methods add
  // the entity-scoped DocumentType clause, so the frontend just forwards user/column filters.
  const isVms = entityType === "VmsRequest" || entityType === "VmsEmail"
    || entityType === "ClientRequest" || entityType === "AtsPlacementRequest"

  if (stage && !isVms) {
    // Standard stage: scope to Assignment documents for the given stage.
    filterConditions.push("DocumentType eq 'Assignment'")

    // Include stage unless the custom filter already specifies one.
    const customHasStage = Array.isArray(customFilter)
      ? customFilter.some(f => /\bStage\s+eq\s+'/i.test(f))
      : false
    if (!customHasStage) {
      filterConditions.push(`Stage eq '${stage}'`)
    }
  }
  // When stage is undefined the customFilter is a complete, self-contained
  // OData expression (e.g. a fixedFilter stage). Skip the Assignment/Stage
  // defaults so they don't conflict with the caller's own DocumentType scope.

  // Add any additional custom filters (already formatted OData expressions)
  if (customFilter && customFilter.length > 0) {
    filterConditions.push(...customFilter)
  }

  return filterConditions.length > 0 ? filterConditions.join(' and ') : null
}

/**
 * Sanitizes and validates search query for OData filters
 * @param query - Raw search query from user input
 * @returns Sanitized query safe for OData interpolation, or null if invalid
 */
const sanitizeSearchQuery = (query: string): string | null => {
  // Trim whitespace
  const trimmed = query.trim();
  
  // Reject if too short or too long
  if (trimmed.length < 2 || trimmed.length > 50) {
    return null;
  }
  
  // Remove or normalize control characters (keep only printable characters)
  const normalized = trimmed.replace(/[\x00-\x1F\x7F-\x9F]/g, '');
  
  // Escape single quotes for OData (replace ' with '')
  const escaped = normalized.replace(/'/g, "''");
  
  // Convert to lowercase for case-insensitive search
  return escaped.toLowerCase();
}

/**
 * Custom hook for Workflow API operations
 * Simple wrapper around WorkflowApiService
 */
export function useWorkflowApi() {
  const { workflowApi, fboReportApi, mainApi, atsServiceApi } = useApi()
  const { entityType } = useEntityType()

  // Search workflow items with parameters
  // Memoized — only changes when workflowApi changes (i.e. token refresh).
  // This prevents downstream useCallback/useEffect chains from re-running
  // on every render (e.g. useStageFacets → refreshStageCounts).
  const searchWorkflowItems = useCallback(async (
    stage: WorkflowStage | undefined,
    customFilter: string[] | undefined,
    fieldConfigurations: FieldConfiguration[],
    page: number = 1,
    pageSize: number = 5,
    search: string = '*',
    select?: string,
    filter?: string,
    orderBy?: string,
    facetKeys?: string[],
    // Overrides the provider-derived entity type for callers that need to query
    // outside their provider's scope — e.g. the global worker search (mounted
    // under the root WorkOrder provider) fanning out to the combined 'Requests'
    // pseudo entity type.
    entityTypeOverride?: string
  ): Promise<WorkflowApiResponse> => {
    // Default newest-first sort. The field differs per entity:
    //   • VmsRequest and ClientRequest rows share gw-client-request-index, which doesn't carry
    //     CreatedDateTime — so we sort by RequestDate (mirrored from the
    //     originating email's CreatedDateTime by the projection).
    //   • VmsEmail rows live in gw-vms-email-index, which *does* have
    //     CreatedDateTime but does NOT have RequestDate (that's a request-side
    //     field). Sorting by RequestDate would 400 with "Could not find a
    //     property named 'RequestDate'".
    //   • Everything else (WorkOrder) → CreatedDateTime desc.
    const defaultOrderBy =
      entityType === "AtsPlacementRequest"
        // ATS rows on the shared index carry no RequestDate (not projected) — sort by the
        // ATS CreatedOn stamp, which is in the index. Avoids a 400 on CreatedDateTime.
        ? 'CreatedOn desc'
        : entityType === "VmsRequest" || entityType === "ClientRequest"
          ? 'RequestDate desc'
          : 'CreatedDateTime desc'

    const searchRequest: WorkflowSearchRequest & { entityType?: string } = {
        select: select || API_SELECT_FIELDS,
        filter: filter || buildFilterFromStageAndFilters(stage, customFilter, entityType),
        orderBy: orderBy ?? defaultOrderBy,
        top: pageSize,
        skip: (page - 1) * pageSize,
        search,
        facets: facetKeys ?? null,
        count: true,
        // Entity type comes from EntityTypeProvider — defaults to "WorkOrder" for the
        // main grid; nested providers (e.g. the VMS tab) override to "VmsRequest" or
        // "VmsEmail" so the backend dispatches to the right index / Cosmos query.
        // Callers can override per-call (see entityTypeOverride param).
        entityType: entityTypeOverride ?? entityType,
      }

      const responseData = await workflowApi.post<any>('/api/workflow/v2/search', searchRequest)

      // Handle different possible response structures
      let apiData: any[]
      let totalCount: number = 0

      if (Array.isArray(responseData)) {
        apiData = responseData
        totalCount = responseData.length
      } else if (responseData && Array.isArray(responseData.data)) {
        apiData = responseData.data
        totalCount = responseData['@odata.count'] || responseData.totalCount || responseData.data.length
      } else if (responseData && Array.isArray(responseData.value)) {
        apiData = responseData.value
        totalCount = responseData['@odata.count'] || responseData.totalCount || responseData.value.length
      } else {
        const formatError = new Error('API response is not in expected format')
        logger.error(formatError, { hook: 'searchWorkflowItems' })
        throw formatError
      }

      // Transform API response to our internal format
      const transformedData = apiData.map(transformApiResponseToWorkflowItem)

      // Extract facets if present
      let facets: Record<string, { value: string; count: number }[]> | undefined = undefined;
      if (responseData['@search.facets'] && typeof responseData['@search.facets'] === 'object') {
        facets = responseData['@search.facets'];
      }

      // Calculate pagination info based on actual total count
      const hasNextPage = (page * pageSize) < totalCount
      const hasPreviousPage = page > 1

      return {
        data: transformedData,
        totalCount,
        hasNextPage,
        hasPreviousPage,
        facets
      }
  }, [workflowApi])

  /**
   * Trigger an .xlsx export from POST /api/workflow/v2/export. Each `sheet`
   * entry becomes a worksheet; columns are shared across sheets and supplied
   * as ordered `fieldConfigurationIds`. The server enforces a 10 000-row cap
   * across all sheets combined; oversize requests come back as 413 with a
   * structured body (rowCount + syncCap + message) which the caller surfaces
   * in the UI.
   *
   * The response is a binary stream (Content-Type: ...spreadsheetml.sheet),
   * NOT JSON — we bypass the workflowApi.post<T> typed-json convenience and
   * call fetchWithAuth directly so we can read the Blob. Caller is responsible
   * for any UX around the download (filename comes from Content-Disposition,
   * which the server already set, but we also accept an override).
   */
  const exportWorkflowItems = useCallback(
    async (request: {
      entityType: string
      sheets: Array<{
        stageCode: string
        stageTitle?: string
        filter?: string
        orderBy?: string
        search?: string
        /** Per-sheet column selection — overrides the request-level default when set. */
        fieldConfigurationIds?: string[]
      }>
      /** Request-level default columns. Used as fallback when a sheet doesn't supply its own. */
      fieldConfigurationIds: string[]
      /**
       * Per-column display-label overrides (the client custom-field relabels the grid applies in-browser).
       * The export header row prefers these; unmatched columns fall back to the server's generic label.
       */
      columnLabelOverrides?: Array<{
        fieldConfigurationId: string
        columnNameEnglish?: string
        columnNameFrench?: string
      }>
      filename?: string
      language?: string
    }): Promise<{ ok: true; blob: Blob; filename: string } | { ok: false; status: number; body: any }> => {
      // workflowApi.post returns the parsed JSON for application/json responses
      // and the raw Response object for everything else. Since we expect a
      // binary attachment on success, the "raw Response" branch is the happy
      // path; the 413 over-cap branch comes back as JSON.
      try {
        // Send the user's IANA timezone so the backend converts DateTime columns from
        // stored UTC into local time — matching the grid's local-time display. Guarded
        // for non-browser/SSR contexts (the export only runs client-side, but be safe).
        const timeZone = typeof Intl !== "undefined"
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : undefined
        const response = await workflowApi.post<Response | any>('/api/workflow/v2/export', { ...request, timeZone })

        // Success path: workflowApi returns the raw Response when the server
        // sends a non-JSON content type. Pull the blob + the filename from
        // Content-Disposition.
        if (response instanceof Response) {
          const blob = await response.blob()
          // Parse `attachment; filename="Tasks_2026-05-29.xlsx"` — fall back
          // to the request's filename (or a generic default) if the header
          // is missing or malformed.
          const disposition = response.headers.get('content-disposition') || ''
          const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i)
          const filename = match ? decodeURIComponent(match[1]) : (request.filename || 'export.xlsx')
          return { ok: true, blob, filename }
        }

        // Unexpected JSON-on-success — treat as failure with the parsed body
        // so callers can surface a sensible message instead of crashing.
        return { ok: false, status: 200, body: response }
      } catch (err: any) {
        // The fetchWithAuth wrapper throws ApiError on non-2xx with the parsed
        // body in `detail` (or undefined for non-JSON errors). The 413 over-
        // cap branch is the one we specifically care about — the modal turns
        // it into a "narrow your filter" prompt rather than a generic toast.
        if (err && typeof err === 'object' && 'status' in err) {
          return { ok: false, status: Number(err.status) || 0, body: err.detail ?? err.message }
        }
        throw err
      }
    },
    [workflowApi],
  )

  // Update a workflow item field
  // V2: Accepts the full item to determine the correct ID based on DocumentType.
  //
  // The DocumentType is also forwarded to the backend as EntityType so the
  // server can dispatch the update to the right service (VMS rows live in
  // their own container — without EntityType the default WorkOrder pipeline
  // 404s on them).
  const updateWorkflowItemField = async (
    item: any,
    field: string,
    value: any
  ): Promise<{ successUpdateCount: number; failedUpdateCount: number; messages: string[] }> => {
    let reportId: string;

    const rawDocType = (item.documentType || item.DocumentType || '').toString();
    const docTypeLower = rawDocType.toLowerCase();
    const reportIdFromItem = item.currentWorkflowHubReportId || item.CurrentWorkflowHubReportId || item.currentworkflowhubreportid;
    // RequestId carries the source Cosmos doc id for entity types whose
    // search-index id was reshaped by the alignment work (VmsRequest +
    // ClientRequest, see SearchIndexId.Generate). For those, item.id is the
    // SHA256-derived index guid and would 404 against the backend's
    // /report/{reportId} route which addresses the source doc.
    const requestId = item.requestId || item.RequestId;

    // ATS field edits don't go through the WorkflowService report endpoint (it rejects
    // AtsPlacementRequest). AtsService owns the canonical ATS doc, so route to its
    // consolidated PATCH endpoint with a JSON Patch on the FBO-editable surface (custom-field
    // slots, AssignedToUser, etc.). recordId is the canonical doc id (RequestId), NOT the SHA
    // index id (item.id). AtsService then re-projects the index + (per flags) the Routine
    // handles audit/shadow/automations off the change feed.
    if (docTypeLower === 'atsplacementrequest') {
      const recordId = requestId || item.id;
      const patchOps = [{ op: 'replace', path: `/${field}`, value }];
      // JSON Patch media type (RFC 6902) — the AtsService PATCH endpoint binds
      // JsonPatchDocument<AtsPlacementEditableDto>, which expects application/json-patch+json.
      await atsServiceApi.patch<unknown>(
        `/api/placement-requests/${recordId}`,
        patchOps,
        { headers: { "Content-Type": "application/json-patch+json" } },
      );
      return { successUpdateCount: 1, failedUpdateCount: 0, messages: [] };
    }

    if (docTypeLower === 'assignment' && reportIdFromItem) {
      reportId = reportIdFromItem;
    } else if ((docTypeLower === 'vmsrequest' || docTypeLower === 'clientrequest') && requestId) {
      reportId = requestId;
    } else if (item.id) {
      reportId = item.id;
    } else {
      reportId = item.id;
    }

    return workflowApi.put<{ successUpdateCount: number; failedUpdateCount: number; messages: string[] }>(
      `/api/workflow/v2/report/${reportId}`,
      {
        Updates: { [field]: value },
        // Forward the PascalCase doc type — server constants are "VmsRequest" /
        // "VmsEmail" / "WorkOrder" / "ClientRequest". When the field is empty
        // (legacy items without documentType) we omit it so the server falls
        // back to its historical WorkOrder pipeline.
        ...(rawDocType ? { EntityType: rawDocType } : {}),
      },
    )
  }

  // Bulk update workflow items
  const bulkUpdateWorkflowItems = async (
    itemIds: string[], 
    field: string, 
    value: any
  ): Promise<WorkflowItem[]> => {
    const notImplementedError = new Error('Bulk update API endpoint not yet implemented')
    logger.error(notImplementedError, { hook: 'bulkUpdateWorkflowItems' })
    throw notImplementedError
  }

  // Bulk update reports (new endpoint)
  // V2: Accepts items with full data to determine the correct IDs based on
  // DocumentType. The batch's entity type is forwarded so the server can
  // dispatch the whole batch to the matching service (VMS / WorkOrder).
  // Assumption: all items in one call share an entity type — the grid is
  // filtered to one tab at a time, so cross-type batches don't occur today.
  const bulkUpdateReports = async (
    items: { item: any; updates: Record<string, any> }[]
  ): Promise<any> => {
    const transformedUpdates = items.map(({ item, updates }) => {
      let reportId: string;

      const docTypeLower = (item.documentType || item.DocumentType || '').toString().toLowerCase();
      const reportIdFromItem = item.currentWorkflowHubReportId || item.CurrentWorkflowHubReportId || item.currentworkflowhubreportid;
      // Same id-resolution rule as updateWorkflowItemField — for entities
      // whose search-index id is SHA256-derived (VmsRequest, ClientRequest
      // post-alignment), the backend bulk endpoint expects the source
      // Cosmos doc id which lives at requestId. item.id here is the index
      // guid and would 404 in the per-row patch loop on the server.
      const requestId = item.requestId || item.RequestId;
      if (docTypeLower === 'assignment' && reportIdFromItem) {
        reportId = reportIdFromItem;
      } else if ((docTypeLower === 'vmsrequest' || docTypeLower === 'clientrequest' || docTypeLower === 'atsplacementrequest') && requestId) {
        // ATS bulk also keys on the canonical doc id (RequestId), not the SHA index id.
        reportId = requestId;
      } else {
        reportId = item.id;
      }

      return { id: reportId, updates };
    });

    // Pull the batch's entity type off the first item. Mixed-type batches would
    // need per-item EntityType + server-side fan-out; today the grid doesn't
    // produce them, but if that changes we'd promote EntityType onto each item.
    const batchEntityType = items.length > 0
      ? (items[0].item.documentType || items[0].item.DocumentType || '').toString()
      : '';

    // ATS bulk goes to AtsService (it owns the canonical doc + editable-surface validation),
    // not the WorkflowService report bulk endpoint (which rejects AtsPlacementRequest). Same
    // { updates: [{ id, updates }] } shape; AtsService applies each via its PATCH path.
    if (batchEntityType.toLowerCase() === 'atsplacementrequest') {
      // AtsService serializes responses PascalCase (its AddJsonOptions sets
      // PropertyNameCaseInsensitive for input binding but NOT a camelCase naming policy for
      // output), so the bulk result comes back as { SuccessUpdateCount, FailedUpdateCount,
      // Messages }. WorkflowService (CR/VMS) returns camelCase, and every consumer of
      // bulkUpdateReports reads camelCase — so normalize the ATS shape here, otherwise the
      // counts read as undefined and every bulk action looks like it failed.
      const r = await atsServiceApi.put<any>('/api/placement-requests/bulk', {
        updates: transformedUpdates,
      });
      return {
        successUpdateCount: r?.successUpdateCount ?? r?.SuccessUpdateCount ?? 0,
        failedUpdateCount: r?.failedUpdateCount ?? r?.FailedUpdateCount ?? 0,
        messages: r?.messages ?? r?.Messages ?? [],
      };
    }

    return workflowApi.put<any>('/api/workflow/v2/reports/bulk', {
      updates: transformedUpdates,
      ...(batchEntityType ? { EntityType: batchEntityType } : {}),
    });
  };

  // Perform stage transition via action
  // V2: Accepts full items to determine the correct IDs based on DocumentType
  const performStageTransition = async (
    actionCode: string,
    items: any[],
    notes?: string
  ): Promise<{
    successCount: number;
    failedCount: number;
    results: Array<{
      itemId: string;
      success: boolean;
      errorMessage?: string;
    }>;
  }> => {
    // Shared with buildSentIdToLocalIdMap (workflow-grid's post-action
    // optimistic updates) — the two MUST stay in lockstep, otherwise the
    // itemIds the API echoes back can't be translated to grid row ids and
    // the optimistic status flip silently targets nothing.
    const itemIds = items.map(item => resolveStageTransitionItemId(item)).filter(Boolean);

      if (itemIds.length === 0) {
        const noIdsError = new Error('No valid item IDs found. Please ensure items have valid ID properties.')
        logger.error(noIdsError, { hook: 'performStageTransition', actionCode })
        throw noIdsError
      }

      // Use the documentType from the first row as the batch entityType. The
      // backend uses it to route into VmsRequestService (Discard) vs the
      // default WorkOrder action lookup. Mixed-entity batches aren't a real
      // case today — the kebab actions are filtered per row, and bulk action
      // selections come from a single tab.
      const batchEntityType = (items[0]?.documentType || items[0]?.DocumentType || '') as string;

      return workflowApi.post<any>('/api/workflow/v2/stage-transitions', {
        actionCode,
        itemIds,
        notes,
        ...(batchEntityType ? { EntityType: batchEntityType } : {}),
      });
  };

  // Fetch workflow configuration.
  //
  // Goes through `coalesceWorkflowConfigFetch` so concurrent callers for the
  // same URL share a single network round-trip. The canonical case: the VMS
  // tab's `useVmsConfig` hook firing in parallel with the inner
  // `WorkflowProvider`'s mount-time refreshConfiguration — both target the
  // exact same `/configurations?entityType=...` URL and used to each pay
  // their own request. Sort lives inside the coalesced fetcher so the shared
  // resolved value is already in display order for every consumer.
  const fetchWorkflowConfiguration = async (viewId?: string | null, preferUserState?: boolean): Promise<WorkflowConfiguration> => {
    let url = '/api/workflow/v2/configurations'
    const params: Record<string, string> = { entityType }
    if (viewId) {
      params.viewId = viewId
    }
    // Refresh-of-the-same-view (Requests-group cross-tab refresh): keep the
    // user's transient state instead of resetting to the view's saved settings.
    if (preferUserState) {
      params.preferUserState = 'true'
    }
    const query = new URLSearchParams(params).toString()
    url += `?${query}`

    return coalesceWorkflowFetch(url, async () => {
      const config = await workflowApi.get<WorkflowConfiguration>(url)
      if (config && Array.isArray(config.stageConfigurations)) {
        config.stageConfigurations.forEach((stageConfig: any) => {
          if (Array.isArray(stageConfig.fieldConfigurations)) {
            stageConfig.fieldConfigurations.sort((a: any, b: any) => a.order - b.order);
          }
        });
      }
      return config;
    })
  }

  // Update a field configuration
  const updateFieldConfiguration = async (
    id: string,
    fieldConfiguration: any,
    // null = "leave stage membership untouched". An ARRAY is the complete desired set — any stage not
    // in it has the field removed server-side, so never pass [] unless you mean "remove from all".
    stageIds: string[] | null,
    currentViewSettingsId?: string | null
  ): Promise<any> => {
    const requestPayload = {
      fieldConfiguration,
      stageIds,
      currentViewSettingsId,
      // The grid/view entity type the edit is happening on (VmsRequest/CR/ATS/WorkOrder). The
      // backend resolves the selected stage(s) against THIS entity's stages, not the field's own
      // EntityType — required so a tenant-wide custom field attaches to the pre-WO "Requests" stage.
      viewEntityType: entityType,
    };
 
    return await workflowApi.put<any>(
      `/api/workflow/v2/field/${id}`,
      requestPayload
    )
  }

  // Create custom field configuration
  const createCustomFieldConfiguration = async (payload: any): Promise<any> => {
    const requestPayload = { ...payload, entityType };
    
    return await workflowApi.post<any>('/api/workflow/v2/field/custom', requestPayload)
  }

  // Fetch assigned users. Backend branches on entityType so VMS rows return
  // VMS assignees (from the VmsRequests container) instead of WorkOrder/
  // Assignment ones — without the param, the call lands in the legacy path
  // and the Assigned To dropdown shows the wrong pool on the VMS grid.
  // Coalesce concurrent fetches by URL — without it React 18 Strict Mode
  // (dev) and the two parallel VMS sub-tab providers each fire their own
  // /assigned-users request for the same entityType. Same in-flight cache
  // pattern as /configurations and /views above.
  const fetchAssignedUsers = async (entityType?: string): Promise<AssignedUser[]> => {
    const url = entityType
      ? `/api/workflow/v2/assigned-users?entityType=${encodeURIComponent(entityType)}`
      : '/api/workflow/v2/assigned-users'
    return coalesceWorkflowFetch(url, async () => {
      const assignedUsers = await workflowApi.get<AssignedUser[]>(url)
      return Array.isArray(assignedUsers) ? assignedUsers : [];
    })
  }

  // Update stage field order
  const updateStageFieldOrder = async (stageId: string, fieldOrder: string[]): Promise<void> => {
    await workflowApi.put<void>(`/api/workflow/v2/stage/${stageId}/fields`, fieldOrder)
  }

  // Delete field configuration
  const deleteFieldConfiguration = async (fieldConfigId: string): Promise<void> => {
    try {
      await workflowApi.delete<void>(`/api/workflow/v2/field/${fieldConfigId}/reset`)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete field configuration'
      const wrappedError = new Error(errorMsg)
      logger.error(wrappedError, { hook: 'deleteFieldConfiguration', fieldConfigId })
      throw wrappedError
    }
  }

  // Toggle field visibility on stage
  const toggleFieldVisibility = async (stageId: string, fieldConfigurations: string[]): Promise<void> => {
    await workflowApi.put<void>(`/api/workflow/v2/stage/${stageId}/fields`, fieldConfigurations)
  }

  // Fetch search suggestions using the Azure suggester.
  // entityType routing (backend dispatch): absent = WorkOrder index only;
  // 'Requests' = the unified pre-onboarding index; 'All' = both suggesters
  // queried server-side in parallel and merged — one round-trip for the
  // debounced global-search autocomplete. NOTE: the Filter body field is NOT
  // forwarded to the suggester by the backend — don't rely on it for scoping.
  const fetchSearchSuggestions = async (
    searchText: string,
    field: string,
    top: number = 5,
    filter?: string,
    suggestionEntityType?: string
  ): Promise<SuggestionsApiResponse> =>
    workflowApi.post<SuggestionsApiResponse>('/api/workflow/v2/suggestions', {
      ColumnCode: field,
      SearchValue: searchText,
      Top: top,
      Filter: filter,
      EntityType: suggestionEntityType,
    });

  // Fetch views. Coalesced through the shared in-flight cache so concurrent
  // callers for the same entityType share a single network round-trip —
  // covers React 18 Strict Mode's dev-only double-mount and the two parallel
  // VMS sub-tab providers each requesting their own views on initial paint.
  const fetchViews = async (): Promise<UserViewsResponse> => {
    const url = `/api/workflow/v2/views?${new URLSearchParams({ entityType }).toString()}`
    return coalesceWorkflowFetch(url, () => workflowApi.get<UserViewsResponse>(url))
  }

  const fetchView = async (viewId: string): Promise<any> =>
    workflowApi.get<any>(`/api/workflow/v2/views/${viewId}?${new URLSearchParams({ entityType }).toString()}`);

  // Property-spread order: closure entityType first, request spread second —
  // so callers can override entityType by setting it on the request body.
  // Cross-tab sibling saves (Requests group "also save changes on:") use
  // this to target a different entity than the active tab. Single-entity
  // saves (the common case) leave request.entityType undefined and pick up
  // the closure value as before.
  const saveViewSettings = async (request: SaveViewSettingsRequestDto): Promise<any> =>
    workflowApi.post<any>('/api/workflow/v2/view-settings', { entityType, ...request });

  /**
   * Group save for unified Requests-style views. The caller supplies one
   * `perEntity` payload per sibling (typically the current tab's entity + the
   * other group members with empty StageConfigurations so the BE auto-creates
   * the sibling docs). Returns per-sibling success/error so the FE can
   * surface partial failures and offer retry just for the failed entries.
   *
   * For "save for this tab only" semantics on a group view, callers stick
   * with {@link saveViewSettings} above — supplying the sibling's existing
   * `id` + `viewGroupCode` updates only that one doc.
   */
  const saveViewGroup = async (request: SaveViewGroupRequestDto): Promise<SaveViewGroupResponseDto> =>
    workflowApi.post<SaveViewGroupResponseDto>('/api/workflow/v2/view-settings/group', request);

  // Tag the UserViewState save with the active entityType so the backend
  // stores it under its own (user, entityType) doc instead of silently
  // defaulting to WorkOrder. Without this, reordering columns on the VMS
  // (or any non-WorkOrder) tab wrote the saved order into the WorkOrder
  // doc — and the VmsRequest configuration read (strict EntityType match)
  // never saw it, so the grid kept rendering the seed default order.
  const saveViewState = async (request: SaveViewSettingsRequestDto): Promise<any> =>
    workflowApi.post<any>('/api/workflow/v2/view-state', { ...request, entityType });

  const deleteViewSettings = async (id: string): Promise<void> =>
    workflowApi.delete<void>(`/api/workflow/v2/view-settings/${id}`);

  const updateViewVisibility = async (id: string, remove: boolean): Promise<void> =>
    workflowApi.put<void>(`/api/workflow/v2/view/${id}?remove=${remove}`, {});

  // Search users for private column authorization
  const searchUsers = async (searchQuery: string): Promise<UserReportDto[]> => {
    const sanitizedQuery = sanitizeSearchQuery(searchQuery);
      if (!sanitizedQuery) {
        return [];
      }

      const filterConditions = [
        `((contains(tolower(FirstName),'${sanitizedQuery}')) or (contains(tolower(LastName),'${sanitizedQuery}')) or (contains(tolower(PrimaryEmail),'${sanitizedQuery}')))`,
        `((ProfileTypeId eq 2) or (ProfileTypeId eq 99))`
      ].join(' and ');

      const params = new URLSearchParams({
        '$orderby': 'ContactId',
        '$top': '100',
        '$select': 'EntityId,FirstName,LastName,PrimaryEmail',
        '$filter': filterConditions,
        '$count': 'true'
      });

      const response = await fboReportApi.get<UserSearchResponse>(`/report/33?${params.toString()}`);
      
      // Transform the API response to UserReportDto format
      return response.value.map((user: UserSearchApiResponse): UserReportDto => ({
        profileId: user.EntityId,
        fullName: `${user.FirstName} ${user.LastName}`.trim() || user.PrimaryEmail,
        email: user.PrimaryEmail
      }));

  };

  // Shared org search backing both the Client and WorkerOrganization grid editors
  // (and client-facing view scoping). Role scoping uses the same
  // `contains(tolower(OrganizationRoleIds),'<id>')` pattern the rest of the app uses for
  // the report/34 org pickers — not modified here. Role ids: 1 = Client;
  // 2/3/4/5 = worker-org (supplier) types.
  const searchOrganizations = async (
    searchQuery: string,
    roleMatchClause?: string,
  ): Promise<{ entityId: number; displayName: string; organizationCode: string }[]> => {
    const sanitizedQuery = sanitizeSearchQuery(searchQuery);
    if (!sanitizedQuery) return [];

    const nameClause = `(contains(tolower(DisplayName),'${sanitizedQuery}') or contains(tolower(LegalName),'${sanitizedQuery}'))`;
    const filterConditions = roleMatchClause ? `${nameClause} and ${roleMatchClause}` : nameClause;

    const params = new URLSearchParams({
      '$orderby': 'EntityId desc',
      '$top': '100',
      '$select': 'EntityId,DisplayName,LegalName,OrganizationCode',
      '$filter': filterConditions,
      '$count': 'true'
    });

    const response = await fboReportApi.get<any>(`/report/34?${params.toString()}`);

    return (response.value || []).map((org: any) => ({
      entityId: org.EntityId,
      displayName: org.DisplayName || org.LegalName || '',
      organizationCode: org.OrganizationCode || '',
    }));
  };

  // Client org search — scoped to role 1 (Client), same as the app's standard org picker.
  const searchClientOrganizations = (searchQuery: string) =>
    searchOrganizations(searchQuery, "(contains(tolower(OrganizationRoleIds),'1'))");

  // Worker-organization (supplier) org search — any of roles 2/3/4/5 (same contains
  // pattern, OR-combined across the supplier role ids).
  const searchWorkerOrganizations = (searchQuery: string) =>
    searchOrganizations(
      searchQuery,
      "(contains(tolower(OrganizationRoleIds),'2') or contains(tolower(OrganizationRoleIds),'3') or contains(tolower(OrganizationRoleIds),'4') or contains(tolower(OrganizationRoleIds),'5'))");

  // VMS email preview. Calls the server-side decrypt endpoint and returns
  // the body + attachment metadata. Body bytes for attachments are NOT
  // fetched here — that would be a separate gated download endpoint.
  const fetchVmsEmailPreview = async (
    emailId: string
  ): Promise<VmsEmailPreview | null> => {
    if (!emailId) return null;
    try {
      return await workflowApi.get<VmsEmailPreview>(`/api/vms/emails/${emailId}/preview`);
    } catch (error) {
      if (error instanceof Error) logger.error(error, { hook: 'fetchVmsEmailPreview', emailId })
      return null;
    }
  };

  const fetchClientCustomFields = async (
    organizationId: number,
    entityType: number = 17
  ): Promise<ClientCustomFieldsResponse | null> => {
    try {
      const url = `/api/ClientBasedEntityCustomField/GetCustomFields?organizationId=${organizationId}&entityType=${entityType}`;
      const response = await mainApi.get<ClientCustomFieldsResponse>(url);
      return response;
    } catch (error) {
      console.error('Failed to fetch client custom fields:', error);
      if (error instanceof Error) logger.error(error, { hook: 'fetchClientCustomFields', organizationId, entityType })
      return null;
    }
  };

  // Fetch child WorkOrders for an Assignment
  const fetchChildWorkOrders = async (
    fboAssignmentId: number,
    select?: string
  ): Promise<WorkflowApiResponse> => {
    const filterConditions = [
      // gw-sync now writes VmsRequest / ClientRequest rows with their own
      // DocumentType values (not WorkOrder+EntityType discriminator), so
      // pinning DocumentType='WorkOrder' is the natural exclusion — no
      // separate EntityType filter needed.
      "DocumentType eq 'WorkOrder'",
      `FboAssignmentId eq ${fboAssignmentId}`,
      "(RequestType eq 'Onboarding' or RequestType eq 'Extension' or RequestType eq null)"
    ]

    const searchRequest: WorkflowSearchRequest = {
      select: select || API_SELECT_FIELDS,
      filter: filterConditions.join(' and '),
      orderBy: 'CreatedDateTime desc',
      top: 100,
      skip: 0,
      search: '*',
      facets: null,
      count: true
    }

    const responseData = await workflowApi.post<any>('/api/workflow/v2/search', searchRequest)

    let apiData: any[]
    let totalCount: number = 0

    if (Array.isArray(responseData)) {
      apiData = responseData
      totalCount = responseData.length
    } else if (responseData && Array.isArray(responseData.data)) {
      apiData = responseData.data
      totalCount = responseData['@odata.count'] || responseData.totalCount || responseData.data.length
    } else if (responseData && Array.isArray(responseData.value)) {
      apiData = responseData.value
      totalCount = responseData['@odata.count'] || responseData.totalCount || responseData.value.length
    } else {
      const formatError = new Error('API response is not in expected format')
        logger.error(formatError, { hook: 'fetchChildWorkOrders', fboAssignmentId })
      throw formatError
    }

    const transformedData = apiData.map(transformApiResponseToWorkflowItem)

    return {
      data: transformedData,
      totalCount,
      hasNextPage: false,
      hasPreviousPage: false
    }
  }

  // Validate ATS Placement ID
  const validateAtsPlacement = async (atsSourceId: number, atsPlacementId: number): Promise<any> =>
    workflowApi.get<any>(`/api/workflow/v2/validate-ats-placement?atsSourceId=${atsSourceId}&atsPlacementId=${atsPlacementId}`)

  const fetchRelatedClientRequests = useCallback(async (
    fboAssignmentId: number
  ): Promise<ChangeRequestDto[]> => {
    const responseData = await workflowApi.get<any>(
      `/api/workflow/v2/client-requests/${fboAssignmentId}`
    )

    const apiData: RelatedRequestApiDto[] = Array.isArray(responseData?.value) ? responseData.value : []
    return apiData.map(transformRelatedRequestToChangeRequest)
  }, [workflowApi])

  const extendWorkOrder = async (
    fboAssignmentId: number,
    atsPlacementId: number | null,
    confirmExtendExistingExtension: boolean = false
  ): Promise<any> => {
    try {
      const response = await workflowApi.post<any>('/api/workflow/v2/extend-work-order', {
        fboAssignmentId,
        atsPlacementId,
        confirmExtendExistingExtension,
      })
      const customPayload = response.customPayload ?? response.CustomPayload ?? null
      const existing = response.existingExtension ?? response.ExistingExtension ?? null
      const existingExtension = existing
        ? {
            fboId: existing.fboId ?? existing.FboId ?? null,
            fboWorkOrderNumber: existing.fboWorkOrderNumber ?? existing.FboWorkOrderNumber ?? null,
            startDate: existing.startDate ?? existing.StartDate ?? null,
            endDate: existing.endDate ?? existing.EndDate ?? null,
          }
        : null
      // Normalize PascalCase backend response to camelCase
      return {
        success: response.success ?? response.Success ?? false,
        errorMessage: response.errorMessage ?? response.ErrorMessage ?? null,
        hasExistingExtension: response.hasExistingExtension ?? response.HasExistingExtension ?? false,
        existingExtension,
        workOrderId: customPayload?.WorkOrderId ?? customPayload?.workOrderId ?? response.workOrderId ?? response.WorkOrderId ?? null,
        workOrderVersionId: customPayload?.WorkOrderVersionId ?? customPayload?.workOrderVersionId ?? response.workOrderVersionId ?? response.WorkOrderVersionId ?? null,
        assignmentId: customPayload?.AssignmentId ?? customPayload?.assignmentId ?? null,
        fboWorkOrderNumber: response.fboWorkOrderNumber ?? response.FboWorkOrderNumber ?? null,
      }
    } catch (error: any) {
      // Extract error details from HTTP error responses (400, 404, 500, etc.)
      const body = error?.body
      if (body && typeof body === 'object') {
        return {
          success: false,
          errorMessage: body.errorMessage ?? body.ErrorMessage ?? body.message ?? error.message ?? null,
          hasExistingExtension: body.hasExistingExtension ?? body.HasExistingExtension ?? false,
          existingExtension: null,
          workOrderId: null,
          workOrderVersionId: null,
          assignmentId: null,
          fboWorkOrderNumber: null,
        }
      }
      return {
        success: false,
        errorMessage: error?.message || 'An unexpected error occurred',
        hasExistingExtension: false,
        existingExtension: null,
        workOrderId: null,
        workOrderVersionId: null,
        assignmentId: null,
        fboWorkOrderNumber: null,
      }
    }
  }

  return {
    searchWorkflowItems,
    exportWorkflowItems,
    updateWorkflowItemField,
    bulkUpdateWorkflowItems,
    fetchWorkflowConfiguration,
    updateFieldConfiguration,
    createCustomFieldConfiguration,
    fetchAssignedUsers,
    updateStageFieldOrder,
    deleteFieldConfiguration,
    toggleFieldVisibility,
    bulkUpdateReports,
    performStageTransition,
    fetchSearchSuggestions,
    fetchViews,
    fetchView,
    saveViewSettings,
    saveViewGroup,
    saveViewState,
    deleteViewSettings,
    updateViewVisibility,
    searchUsers,
    searchClientOrganizations,
    searchWorkerOrganizations,
    fetchVmsEmailPreview,
    fetchClientCustomFields,
    fetchChildWorkOrders,
    validateAtsPlacement,
    extendWorkOrder,
    fetchRelatedClientRequests,
  }
}